import React, {useEffect, useRef} from 'react';
import s from './ShareModal.module.scss';
import {ReactComponent as EyeIcon} from 'images/eye2.svg';
import {ReactComponent as RecentIcon} from 'images/new-icons/purchases.svg';
import {ReactComponent as ServiceIcon} from 'images/new-icons/share-modal/salon.svg';
import {ReactComponent as ProductIcon} from 'images/new-icons/share-modal/bag-outline.svg';
import {ReactComponent as ShareIcon} from 'images/new-icons/share-modal/share.svg';
import Button from 'components/shared/Button';
import Portal from 'components/shared/Portal/Portal';
import {useActions} from 'store/useActions';
import {animated, useSpring} from '@react-spring/web';
import {useDrag} from '@use-gesture/react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { PostFilter } from 'sdk/hooks/useAllPosts';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    post: any;
    setIsShareModalOpen: (isOpen: boolean) => void;
}

const items = [
    {
        icon: <EyeIcon/>,
        title: 'Popular',
        filter: 'most-viewed' as PostFilter,
    },
    {
        icon: <RecentIcon/>,
        title: 'Recent',
        filter: 'recent' as PostFilter,
    },
    {
        icon: <ProductIcon />,
        title: 'Products',
        filter: 'products' as PostFilter,
    },
    {
        icon: <ServiceIcon/>,
        title: 'Services',
        filter: 'services' as PostFilter,
    },
    {
        icon: <ShareIcon/>,
        title: 'Share',
        filter: null,
    },
];

const ShareModal: React.FC<ShareModalProps> = ({
                                                   isOpen,
                                                   onClose,
                                                   post,
                                                   setIsShareModalOpen,
                                               }) => {
    const {t} = useTranslation() as any;
    const modalRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const {setPostFilter} = useActions();

    const [{y}, api] = useSpring(() => ({
        y: window.innerHeight,
        config: {tension: 300, friction: 30},
    }));

    const handleItemClick = (item: typeof items[0]) => {
        if (item.title === 'Share') {
            navigator.share({
                title: 'Share',
                text: 'Share',
                url: window.location.href,
            });
            return;
        }

        if (item.filter) {
            setPostFilter(item.filter);
            onClose();
        }
    };

    useEffect(() => {
        if (isOpen) {
            api.start({y: 0});
            setIsShareModalOpen(true);
        } else {
            api.start({y: window.innerHeight});
            setIsShareModalOpen(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const bind = useDrag(
        ({movement, direction, last, velocity, event}) => {
            const my = movement[1];
            const dy = direction[1];
            
            if (Math.abs(my) > 10) {
                event.stopPropagation();
            }
            if (last) {
                if (my > 100 || (dy > 0 && velocity[1] > 0.5)) {
                    onClose();
                }
            } else {
                api.start({y: Math.max(0, my)});
            }
        },
        {
            from: () => [0, y.get()],
            bounds: {top: 0},
            rubberband: true,
        },
    );

    if (!isOpen) return null;

    return (
        <Portal>
            <animated.div
                {...bind()}
                className={s.overlay}
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    transform: y.to(py => `translateY(${py}px)`),
                    touchAction: 'none',
                    borderTopLeftRadius: '32px',
                    borderTopRightRadius: '32px',
                    zIndex: 2000, 
                }}
            >
                <div ref={modalRef} className={s.modal}>
                    <div className={s.modal_items}>
                        {items.map((item, index) => (
                            <div
                                key={index}
                                className={s.modal_item}
                                onClick={() => handleItemClick(item)}
                            >
                                {item.icon}
                                <p>{t(item.title)}</p>
                            </div>
                        ))}
                    </div>

                    <Button ligtGrey onClick={() => navigate(`/appointment-booking/${post?.salon_profile}`)}>
                        {t('Book Now')}
                    </Button>
                </div>
            </animated.div>
        </Portal>
    );
};

export default ShareModal;